<script setup lang="ts">
/**
 * Security Services Dynamic Page
 *
 * Renders service pages based on slug (e.g., /security-services/cctv)
 * Fetches content from API and renders blocks dynamically.
 */

import type { ServicePageDoc, ContentBlock } from "~~/shared/types";

// Get route params
const route = useRoute();
const slug = computed(() => route.params.slug as string);

// Show context switcher on CCTV pages only
const showCctvSwitcher = computed(() =>
  slug.value === 'cctv' || slug.value === 'residential-cctv'
);

// Fetch page content
const { data: pageData, pending, error } = await useAsyncData<ServicePageDoc | null>(
  `service-security-${slug.value}`,
  async () => {
    try {
      const response = await $fetch(`/api/content/service/security-services/${slug.value}`);
      if (response.success && response.data) {
        return response.data as ServicePageDoc;
      }
      return null;
    } catch (err: unknown) {
      const status = (err as { statusCode?: number; response?: { status?: number } })?.statusCode
        ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  }
);

// API call itself failed transiently (non-404). Surface as 5xx so error.vue
// does NOT emit noindex and Google retries instead of de-indexing.
if (error.value) {
  throw createError({
    statusCode: 503,
    statusMessage: "Service Unavailable",
    fatal: true,
  });
}

// Slug genuinely doesn't exist - 404 + noindex (via error.vue).
if (!pending.value && !pageData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page Not Found",
  });
}

// Transform blocks to inject breadcrumbs into hero
const blocksWithBreadcrumbs = computed<ContentBlock[]>(() => {
  if (!pageData.value) return [];

  // Convert API breadcrumbs format to component format
  const breadcrumbs = pageData.value.breadcrumbs.map((crumb, index, arr) => ({
    label: crumb.name,
    to: index < arr.length - 1 ? crumb.href : undefined,
  }));

  // Inject breadcrumbs into the first hero block
  return pageData.value.blocks.map((block, index) => {
    if (index === 0 && (block.type === "hero" || block.type === "hero-index")) {
      return {
        ...block,
        data: {
          ...block.data,
          breadcrumbs,
        },
      };
    }
    return block;
  });
});

// Hero service context, passed to every BlockRenderer instance so the CCTV
// switcher path (which renders blocks across two renderers) still routes
// residential quote CTAs to the residential-cctv-quote modal variant.
const heroService = computed(() => {
  const hero = blocksWithBreadcrumbs.value.find((block) => block.type === "hero");
  return hero?.data?.service ?? "";
});

// SEO Meta
useSeoMeta({
  title: () => pageData.value?.meta.title || "Security Services | Metwide",
  description: () => pageData.value?.meta.description || "",
  ogTitle: () => pageData.value?.meta.title || "Security Services | Metwide",
  ogDescription: () => pageData.value?.meta.description || "",
  ogImage: () => pageData.value?.meta.ogImage || "/og-image.jpg",
  ogType: "website",
});

// Schema.org structured data (canonical is handled globally in app.vue)
const schemaOrg = computed(() => {
  if (!pageData.value) return null;

  const baseUrl = useSiteUrl()
  const pageUrl = `${baseUrl}/security-services/${slug.value}`;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@graph": [
      // WebPage
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: pageData.value.meta.title,
        description: pageData.value.meta.description,
        inLanguage: "en-AU",
        ...(pageData.value.meta.datePublished
          ? { datePublished: pageData.value.meta.datePublished }
          : {}),
        ...(pageData.value.meta.dateModified
          ? { dateModified: pageData.value.meta.dateModified }
          : {}),
        publisher: { "@id": `${baseUrl}/#organization` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
      },
      // BreadcrumbList
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: pageData.value.breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          // Handle missing href (common for current page breadcrumb)
          item: crumb.href
            ? (crumb.href.startsWith("/") ? `${baseUrl}${crumb.href}` : crumb.href)
            : pageUrl,
        })),
      },
    ],
  };

  // Add Service schema if available
  if (pageData.value.schema) {
    // Resolve meta.ogImage to an absolute URL (Service.image requires it).
    const ogImageRaw = pageData.value.meta.ogImage;
    const ogImageAbs = ogImageRaw
      ? (ogImageRaw.startsWith("http") ? ogImageRaw : `${baseUrl}${ogImageRaw}`)
      : undefined;

    // Build hasOfferCatalog only when schema.offers is populated, so the 30+
    // other security-service pages that share this template are unaffected.
    // Each offer is wrapped in a ListItem because Schema.org Offer has no
    // `position` property; ordered enumerations belong on ListItem.
    // Pricing only emits when minPrice + priceCurrency are both present;
    // indicative "From $X" wording must not propagate to structured data
    // until stakeholders sign off on the canonical value.
    const offers = pageData.value.schema.offers;
    const hasOfferCatalog = offers && offers.length > 0
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: pageData.value.schema.name,
            itemListElement: offers.map((o, i) => {
              const offerNode: Record<string, unknown> = {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: o.name,
                  ...(o.description ? { description: o.description } : {}),
                },
              };
              if (o.minPrice && o.priceCurrency) {
                offerNode.priceSpecification = {
                  "@type": "PriceSpecification",
                  minPrice: o.minPrice,
                  priceCurrency: o.priceCurrency,
                };
              }
              return {
                "@type": "ListItem",
                position: i + 1,
                item: offerNode,
              };
            }),
          },
        }
      : {};

    (schema["@graph"] as Array<Record<string, unknown>>).push({
      "@type": "Service",
      "@id": `${pageUrl}#service`,
      name: pageData.value.schema.name,
      description: pageData.value.meta.description,
      ...(ogImageAbs ? { image: ogImageAbs } : {}),
      provider: { "@id": `${baseUrl}/#organization` },
      url: pageUrl,
      serviceType: pageData.value.schema.serviceTypes,
      // Service.areaServed accepts Text values; emit raw strings so a mix of
      // cities, suburbs, regions, states and countries doesn't get mistyped as City.
      areaServed: pageData.value.schema.areasServed,
      ...hasOfferCatalog,
    });
  }

  return schema;
});

useHead({
  script: schemaOrg.value
    ? [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(schemaOrg.value),
        },
      ]
    : [],
});
</script>

<template>
  <div>
    <!-- Loading State -->
    <div v-if="pending" class="flex min-h-screen items-center justify-center">
      <Icon name="lucide:loader-2" size="48" class="animate-spin text-primary-400" />
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="flex min-h-screen flex-col items-center justify-center px-4"
    >
      <Icon name="lucide:alert-circle" size="64" class="mb-4 text-red-400" />
      <h1 class="mb-2 text-2xl font-bold text-slate-900">Something went wrong</h1>
      <p class="mb-6 text-neutral-600">We couldn't load this page. Please try again.</p>
      <NuxtLink
        to="/security-services"
        class="rounded-full bg-primary-400 px-6 py-3 font-bold text-white hover:bg-primary-500"
      >
        Back to Security Services
      </NuxtLink>
    </div>

    <!-- Page Content -->
    <template v-else-if="pageData">
      <!-- Render hero block first, then context switcher, then remaining blocks -->
      <template v-if="showCctvSwitcher && blocksWithBreadcrumbs.length > 0">
        <!-- Hero block -->
        <BlocksBlockRenderer :blocks="[blocksWithBreadcrumbs[0]]" :service="heroService" />
        <!-- CCTV Commercial / Residential switcher -->
        <CctvContextSwitcher />
        <!-- Remaining blocks -->
        <BlocksBlockRenderer :blocks="blocksWithBreadcrumbs.slice(1)" :service="heroService" />
      </template>
      <!-- Standard rendering for non-CCTV pages -->
      <BlocksBlockRenderer v-else :blocks="blocksWithBreadcrumbs" :service="heroService" />
    </template>
  </div>
</template>